import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import LegalPagesApi from '../../apis/LegalPagesApi';
import LoadingCircle from '../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../components/UnexpectedError/UnexpectedError';
import { LegalPageDto } from '../../generated-types/legalPageDto';
import useApi from '../../lib/useApi';
import ContactCard from './components/ContactCard';

/**
 * The properties for the legal page component
 */
interface LegalViewProps {
  /**
   * The id of the legal page to show. This id is used to retrieve it from the backend.
   */
  pageId: string;

  /**
   * A message specific to the context it's showing for. This should fit: "We encountered an error while loading {errorContext}."
   */
  errorContext: string;
}

/**
 * A view to show a page that contains legal information for the user.
 */
const LegalView = (props: LegalViewProps) => {
  const theme = useTheme();
  const [legalPage, setLegalPage] = useState<LegalPageDto>();
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const legalPagesApi = useApi(LegalPagesApi);

  useEffect(() => {
    setIsLoading(true);
    setShowError(false);
    legalPagesApi
      .GetLegalPage(props.pageId)
      .then(setLegalPage)
      .catch(() => setShowError(true))
      .finally(() => setIsLoading(false));
  }, [legalPagesApi, props.pageId]);

  return (
    <>
      <Box width="100%" marginTop="4em" marginX="1em">
        {showError ? (
          <UnexpectedError errorContext={props.errorContext} />
        ) : (
          <Container sx={{ paddingBottom: '2em' }}>
            <Box boxShadow={4} borderRadius="2px" paddingBottom="1em">
              <Box bgcolor={theme.palette.primary.main} borderRadius="2px">
                <Container sx={{ paddingY: 8, paddingX: { xs: 2, sm: 4 } }}>
                  <Typography
                    id="legal-page-title"
                    variant="h3"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.common.white,
                    }}
                  >
                    {legalPage?.title}
                  </Typography>
                  <Typography
                    gutterBottom
                    sx={{
                      color: theme.palette.common.white,
                    }}
                  >
                    Last updated on{' '}
                    <strong>
                      {legalPage?.lastUpdated.toLocaleDateString()}
                    </strong>
                  </Typography>
                </Container>
                <Box
                  component={'svg'}
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                  x="0px"
                  y="0px"
                  viewBox="0 0 1920 100.1"
                  width={'100%'}
                  marginBottom={-1}
                >
                  <path
                    fill={theme.palette.background.paper}
                    d="M0,0c0,0,934.4,93.4,1920,0v100.1H0L0,0z"
                  ></path>
                </Box>
              </Box>
              <Container
                sx={{
                  paddingTop: '0 !important',
                  paddingX: {
                    xs: 2,
                    sm: 4,
                  },
                  position: 'relative',
                  top: 0,
                }}
              >
                <Box
                  component={Grid}
                  container
                  spacing={4}
                  flexDirection={{ xs: 'column-reverse', md: 'row' }}
                >
                  <Grid item xs={12} md={9}>
                    <Markdown>{legalPage?.markdown}</Markdown>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box
                      position={'sticky'}
                      top={theme.spacing(10)}
                      className={'sticky'}
                    >
                      <ContactCard
                        contactType={legalPage?.title.toLowerCase() ?? ''}
                      />
                    </Box>
                  </Grid>
                </Box>
              </Container>
            </Box>
          </Container>
        )}
      </Box>
      <LoadingCircle isLoading={isLoading} />
    </>
  );
};

export default LegalView;
